import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kSettleOnce } from "../../../Admin/kReusableCoreTS/kSettleOnce";
import { AppExportAnswer, AppExportRules } from "./AppExportRules";
import { AppSignUpApi } from "./AppSignUpApi";
import { AppTimeZone } from "./AppTimeZone";

// AppExport - the browser half of "Export chat": one POST to `/export`, the answer's bytes kept as
// they came (the BOM included), saved through a Blob URL and `<a download>` - the same download route as
// "Save chart" (`AppAssistTools.Save`), which the app's CONTENT-SECURITY-POLICY already allows (`blob:`).
//
// ⭐ LAW 7 - NEVER A SILENT WAIT: the button says "Exporting…" and is disabled in the same click; the note
// under it ticks every 500 ms ("Preparing the file… 3 s", then "Downloading… 1.2 MB (5 s)"), and the loop is
// closed with "Saved "<name>" - N messages. See your downloads." A refusal is the service's own sentence,
// in ink (law 1: validation - "not running", "too many" - the subscriber can act on it; never orange).
//
// ⚠ `arraybuffer`, NEVER `text`: an XHR text decode STRIPS the byte-order mark, and the BOM is what makes
// Excel read "Björn" rather than "BjÃ¶rn". A refusal (JSON) is decoded from the same bytes.
// ⚠ NO CLIENT TIMEOUT - a 20,000-message chat takes a few seconds to read; the service owns the limit.
// ⚠ ITS OWN XHR, like `AppAccountPost`/`AppAssistStreamPost`: the shared REST transport is SharePoint-bound
// (`AppSource.ts`'s header) and `AppAccountPost` hands back text only - this needs bytes, headers, progress.

/** Everything one export needs, gathered by `AppMain` when the button is pressed. */
export class AppExportWho {
    public ServiceUrl: string = "";
    public Phone: string = "";
    public Key: string = "";
    public Page: string = "";
    public Title: string = "";
}

/** The button's click: reads who is asking at CLICK time (a key may arrive after the paint). */
export type AppExportAsk = () => AppExportWho;

export class AppExport {
    /** How often the note ticks - law 7 asks for at least once a second. */
    public static readonly TickMs: number = 500;

    /** Wire the control `AppExportRules.Control` painted. No button (a group, no service) = nothing. */
    public static Wire = (Ask: AppExportAsk): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Button: HTMLElement | null = document.getElementById(AppExportRules.ButtonId);
            if (Button === null) { return; }
            Button.addEventListener("click", new AppExportClick(Ask).OnClick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExport.Wire");
        }
    };

    /** The browser's language tag ("sv-SE"), or "" - `SepFor` then asks ICU's default. */
    public static LocaleNow = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof navigator === "undefined") { return ""; }
            return nz(navigator.language);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExport.LocaleNow");
            return "";
        }
    };
}

/** One button's click handler - one export at a time per button. */
class AppExportClick {
    private Ask: AppExportAsk;
    private Busy: boolean = false;

    public constructor(Ask: AppExportAsk) {
        this.Ask = Ask;
    }

    public OnClick = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Busy === true) { return; }
            const Who: AppExportWho = this.Ask();
            const Url: string = AppSignUpApi.UrlFor(Who.ServiceUrl, AppExportRules.Route);
            if (Url.length === 0) { AppExportJob.Note(AppSignUpApi.NoServiceWhy()); return; }
            this.Busy = true;
            // ⭐ The separator from this browser's locale (`AppExportRules.SepFor`) - what Excel will split on.
            new AppExportJob(Url, AppExportRules.Body(Who.Phone, Who.Key, Who.Page, AppTimeZone.Here(),
                AppExportRules.SepFor(AppExport.LocaleNow()), AppExportRules.HtmlFormat),
                AppExportRules.FileName(Who.Title, AppExportRules.HtmlFormat), this.OnDone).Start();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportClick.OnClick");
        }
    };

    private OnDone = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - releases the button; a gated release would leave
        // it dead for the rest of the visit.
        this.Busy = false;
    };
}

/** One request: the POST, the ticking note, the save. Answers `Done` exactly once. */
class AppExportJob {
    private Url: string;
    private Body: string;
    private Name: string;
    private Done: () => void;
    private Req: XMLHttpRequest | null = null;
    private Started: number = 0;
    private Bytes: number = 0;
    private Timer: number = 0;
    private Result: kSettleOnce<string>;
    /** ⭐ Waiting for the service to finish restoring the archive (a `retry` refusal) - and how many re-asks so far. */
    private Readying: boolean = false;
    private Tries: number = 0;

    public constructor(Url: string, Body: string, Name: string, Done: () => void) {
        this.Url = Url;
        this.Body = Body;
        this.Name = Name;
        this.Done = Done;
        this.Result = new kSettleOnce<string>(this.OnFinished);
    }

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppExportJob.Busy(true);
            this.Started = Date.now();
            this.OnTick();
            this.Timer = window.setInterval(this.OnTick, AppExport.TickMs);
            this.Post();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.Start");
            this.Finish("");
        }
    };

    /** One POST of the body (the first, or again after a `retry` refusal). */
    private Post = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the button stays busy until `Finish`; the catch finishes.
        try {
            if (this.Result.IsPending() === false) { return; }
            this.Readying = false;
            this.Req = new XMLHttpRequest();
            const Req: XMLHttpRequest = this.Req as XMLHttpRequest;
            Req.responseType = "arraybuffer";
            Req.onprogress = this.OnProgress;
            Req.onreadystatechange = this.OnState;
            Req.open("POST", this.Url, true);
            Req.setRequestHeader("Content-Type", "application/json");
            Req.send(this.Body);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.Post");
            this.Finish("");
        }
    };

    private OnTick = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a timer; `Finish` clears it on every path.
        try {
            if (this.Readying === true) { AppExportJob.Note(AppExportRules.ReadyingNote()); return; }
            AppExportJob.Note(AppExportRules.Working((Date.now() - this.Started) / 1000, this.Bytes));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.OnTick");
        }
    };

    private OnProgress = (Event: ProgressEvent): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - progress only; `OnState` finishes.
        try {
            this.Bytes = Event.loaded;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.OnProgress");
        }
    };

    /** ⛔ The body is read whatever the status (`AppExportRules.Parse`). */
    private OnState = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the page is waiting on this answer.
        try {
            const Req: XMLHttpRequest = this.Req as XMLHttpRequest;
            if (Req.readyState !== 4) { return; }
            const Bytes: ArrayBuffer | null = Req.response as ArrayBuffer | null;
            const Type: string | null = Req.getResponseHeader("Content-Type");
            let Json: string = "";
            // ⚠ THE ROW COUNT IS READ ONLY FROM THE FILE'S ANSWER: a refusal (JSON) does not expose `X-WAC-Rows`, and
            // asking for it logs "Refused to get unsafe header" in Chrome (seen by csp-watch, 2026-09-27).
            let RowsText: string | null = null;
            if (AppExportRules.IsFile(Type) === true) { RowsText = Req.getResponseHeader(AppExportRules.RowsHeader); }
            if (AppExportRules.IsFile(Type) === false && Bytes !== null) { Json = new TextDecoder("utf-8").decode(Bytes); }
            const Answer: AppExportAnswer = AppExportRules.Parse(Req.status, Type, Json, RowsText, AppSignUpApi.OursWhy());
            // ⭐ THE RESTART WINDOW (2026-09-29): asked again quietly - never the final "not built" sentence.
            const Again: number = AppExportRules.AgainAfterMs(Answer, this.Tries);
            if (Again > 0) {
                this.Tries = this.Tries + 1;
                this.Readying = true;
                this.OnTick();
                window.setTimeout(this.Post, Again);
                return;
            }
            if (Answer.Ok === false || Bytes === null) { this.Finish(Answer.Why); return; }
            this.Finish(AppExportJob.Save(Bytes as ArrayBuffer, this.Name, Answer.Rows));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.OnState");
            this.Finish("");
        }
    };

    /** The file, saved. The closing line, or `CannotSaveWhy` for a browser with no download support. */
    private static Save = (Bytes: ArrayBuffer, Name: string, Rows: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof Blob === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
                return AppExportRules.CannotSaveWhy();
            }
            const Url: string = URL.createObjectURL(new Blob([Bytes], { type: AppExportRules.HtmlType + ";charset=utf-8" }));
            const Link: HTMLAnchorElement = document.createElement("a");
            Link.href = Url;
            Link.download = Name;
            Link.rel = "noopener";
            // ⚠ `hidden`, never a style: the app's CSP has no 'unsafe-inline' for styles (T9, 2026-09-27).
            Link.hidden = true;
            document.body.appendChild(Link);
            Link.click();
            document.body.removeChild(Link);
            window.setTimeout(new AppExportUrlDrop(Url).OnTimer, 60000);
            return AppExportRules.Done(Name, Rows);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.Save");
            return "";
        }
    };

    /** ⛔ The ONLY place `Done` is called, latched first. The note keeps the last sentence. */
    private Finish = (Note: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the one thing that releases the button.
        try {
            this.Result.Settle(Note);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.Finish");
        }
    };

    private OnFinished = (Note: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: ungated cleanup; Result closes before this callback.
        // A cleanup or owner exception belongs to Finish's existing report boundary.
        if (this.Timer !== 0) { window.clearInterval(this.Timer); this.Timer = 0; }
        AppExportJob.Busy(false);
        AppExportJob.Note(Note);
        this.Done();
    };

    /** The button's busy state - disabled and relabelled in the same frame as the click. */
    private static Busy = (On: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Button: HTMLButtonElement | null = document.getElementById(AppExportRules.ButtonId) as HTMLButtonElement | null;
            if (Button === null) { return; }
            Button.disabled = On;
            Button.setAttribute("aria-busy", String(On));
            // ⛔ Only the label span - the Download icon beside it stays (a button-wide textContent dropped it).
            const Label: Element | null = Button.querySelector("." + AppExportRules.LabelClass);
            if (Label === null) { return; }
            Label.textContent = AppExportRules.ButtonText;
            if (On === true) { Label.textContent = AppExportRules.BusyText; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.Busy");
        }
    };

    /** The note on its own line under the chat header (text only). ⚠ Gone with the page when the owner opens another contact. */
    public static Note = (Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Note: HTMLElement | null = document.getElementById(AppExportRules.NoteId);
            if (Note === null) { return; }
            // ⛔ A live region is written only when its words change (a re-written one is read out again).
            if (Note.textContent === nz(Text)) { return; }
            Note.textContent = nz(Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportJob.Note");
        }
    };
}

/** The object URL of one saved file, revoked a while after the download started. */
class AppExportUrlDrop {
    private Url: string;

    public constructor(Url: string) {
        this.Url = Url;
    }

    public OnTimer = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            URL.revokeObjectURL(this.Url);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppExportUrlDrop.OnTimer");
        }
    };
}
